import type { ReactNode } from 'react'
import { SectionCard } from '../../shared/section-card'
import { useCoachingStore } from '../../store'
import type { ReviewComment } from '../contracts'
import { CritiqueFlag } from '../critique-flag'
import {
  type SuggestedResourceRow,
  SuggestedResources,
  type SuggestedResourceType,
} from './suggested-resources'

/**
 * Deep Dives section (spec §8.5, URL Shortener seed) — a system-design-
 * specific UI payload, not modeled on `ReviewSubmission` (see A1's note on
 * code-section.tsx). Two covered dives render as clickable cards; the
 * missed "DB partitioning" dive renders as a red dashed gap block with its
 * own "+ Comment on gap" target and the shared `SuggestedResources` rows
 * (assign state stored in the slice's `attachments`, keyed
 * `resource:{submissionId}:{resourceId}` — same mechanism
 * `alternative-approaches.tsx` / `hld-diagram.tsx` use for their own
 * toggles, just a distinct key prefix so none of the three can collide).
 */

const COVERED = [
  {
    key: 'ids',
    title: 'ID generation — base62 counter vs hash',
    body: 'Chose a counter-based key service over md5-and-truncate to avoid collisions entirely. KGS pre-generates keys in batches of 10k and hands them to write servers, so no coordination on the hot path.',
    target: 'Deep dives · ID generation',
  },
  {
    key: 'cache',
    title: 'Cache strategy — cache-aside with LRU',
    body: '~20% of links get ~80% of traffic, so a Redis cache-aside layer with LRU eviction should absorb most reads. Sized it at 20% of daily working set (~25 GB).',
    target: 'Deep dives · Cache strategy',
  },
]

const GAP_RESOURCES: Array<{
  id: string
  type: SuggestedResourceType
  label: string
  meta: string
}> = [
  {
    id: 'partition-read',
    type: 'Read',
    label: 'DDIA ch. 6 — Partitioning',
    meta: '~40 min',
  },
  {
    id: 'partition-drill',
    type: 'Drill',
    label: 'Partition-key katas ×3 (sharding set)',
    meta: 'Sprint 15',
  },
  {
    id: 'partition-video',
    type: 'Video',
    label: 'Consistent hashing, explained',
    meta: '12 min',
  },
]

export function DeepDives(props: {
  submissionId: string
  comments?: ReviewComment[]
  anchorRef?: React.Ref<HTMLDivElement>
}): ReactNode {
  const { submissionId, comments = [], anchorRef } = props
  const { openComposer, attachments, toggleAttachment } =
    useCoachingStore().review
  const sectionComments = comments.filter((c) => c.section === 'deep')

  const rows: SuggestedResourceRow[] = GAP_RESOURCES.map((r) => {
    const key = `resource:${submissionId}:${r.id}`
    return {
      id: r.id,
      type: r.type,
      label: r.label,
      meta: r.meta,
      assigned: attachments[key] ?? false,
      onToggleAssign: () => toggleAttachment(key),
    }
  })

  return (
    <SectionCard
      title="Deep dives"
      commentCount={sectionComments.length}
      onAddComment={() =>
        openComposer('deep', 'Deep dives · Overall', 'critique')
      }
      anchorRef={anchorRef}
    >
      {sectionComments.map((c) => (
        <CritiqueFlag key={c.id} comment={c} />
      ))}

      <div className="flex flex-col gap-2.5">
        {COVERED.map((dive) => (
          <button
            key={dive.key}
            type="button"
            onClick={() => openComposer('deep', dive.target, 'critique')}
            className="block w-full rounded-[10px] border border-(--cd-border-2) bg-(--cd-inset) p-3 text-left"
          >
            <div className="mb-1 text-xs font-semibold text-(--cd-text)">
              {dive.title}
            </div>
            <div className="text-[12.5px] leading-relaxed text-(--cd-text-muted)">
              {dive.body}
            </div>
          </button>
        ))}

        <div
          className="rounded-[10px] border border-dashed p-3"
          style={{
            background: 'color-mix(in oklab, var(--cd-red) 5%, transparent)',
            borderColor: 'color-mix(in oklab, var(--cd-red) 35%, transparent)',
          }}
        >
          <div className="mb-1 flex items-center justify-between gap-2.5">
            <div className="text-xs font-semibold text-(--cd-red)">
              DB partitioning — not covered
            </div>
            <button
              type="button"
              onClick={() =>
                openComposer(
                  'deep',
                  'Deep dives · Partitioning gap',
                  'critique',
                )
              }
              className="shrink-0 rounded-full border border-(--cd-border-2) px-2.5 py-1 text-[11.5px] text-(--cd-text-muted)"
            >
              + Comment on gap
            </button>
          </div>
          <div className="mb-2.5 text-[12.5px] leading-relaxed text-(--cd-text-muted)">
            Ran out of time before discussing how 1B rows are partitioned.
          </div>
          <div className="mb-1.5 text-[10.5px] font-bold tracking-wide text-(--cd-purple)">
            ✦ SUGGESTED RESOURCES TO CLOSE THE GAP
          </div>
          <SuggestedResources rows={rows} />
        </div>
      </div>
    </SectionCard>
  )
}
